import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { CoinIcon, EventIcon, StarIcon, ChartIcon, ProfileIcon, CardIcon } from '../components/icons/NavIcons';
import CreditBenefitsInfo from '../components/CreditBenefitsInfo';
import LoadingSpinner from '../components/LoadingSpinner';
import LoginRequired from '../components/LoginRequired';

/**
 * Composant d'autocomplétion d'adresse avec Nominatim
 */
const AddressAutocomplete = ({ value, onChange, required = false }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(value?.formatted_address || '');
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (value?.formatted_address) {
      setSelectedAddress(value.formatted_address);
      setQuery('');
    }
  }, [value]);

  const searchAddress = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        new URLSearchParams({
          q: searchQuery,
          format: 'json',
          addressdetails: '1',
          limit: '5',
          countrycodes: 'fr' // Limiter à la France, ajustez selon vos besoins
        }),
        {
          headers: {
            'Accept-Language': 'fr'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche d\'adresse:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    setSelectedAddress('');
    
    // Annuler la recherche précédente si elle existe
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Planifier une nouvelle recherche après 500ms
    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(newValue);
    }, 500);
  };

  // Nettoyer le timeout quand le composant est démonté
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const selectAddress = (address) => {
    const streetNumber = address.address?.house_number || '';
    const streetName = address.address?.road || '';
    const formattedStreetAddress = `${streetNumber} ${streetName}`.trim();
    
    const addressData = {
      formatted_address: formattedStreetAddress,
      street_number: streetNumber,
      street_name: streetName,
      postal_code: address.address?.postcode || '',
      city: address.address?.city || address.address?.town || address.address?.village || '',
      region: address.address?.state || '',
      country: address.address?.country || 'France',
      latitude: parseFloat(address.lat),
      longitude: parseFloat(address.lon)
    };

    setSelectedAddress(address.display_name);
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    onChange(addressData);
  };

  const clearAddress = () => {
    setSelectedAddress('');
    setQuery('');
    setSuggestions([]);
    
    // Annuler toute recherche en attente
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    onChange({
      formatted_address: '',
      street_number: '',
      street_name: '',
      postal_code: '',
      city: '',
      region: '',
      country: ''
    });
  };

  return (
    <div className="relative">
      {selectedAddress ? (
        <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-300 rounded-lg">
          <span className="flex-1 text-sm text-gray-700">{selectedAddress}</span>
          <button
            type="button"
            onClick={clearAddress}
            className="text-red-500 hover:text-red-700 text-sm font-medium"
          >
            ✕
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => query && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            required={required}
            placeholder={t('createEvent.searchAddress') || 'Rechercher une adresse...'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
          {loading && (
            <div className="absolute right-3 top-3">
              <div className="animate-spin h-5 w-5 border-2 border-bleu1 border-t-transparent rounded-full"></div>
            </div>
          )}
        </>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => selectAddress(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="text-sm text-gray-900">{suggestion.display_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Composant d'affichage du coût de création d'événement
 */
const EventCostDisplay = () => {
  const { t } = useTranslation();
  const [eventCost, setEventCost] = useState(null);
  const [creditAmount, setCreditAmount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchParameters = async () => {
      try {
        const { data, error } = await supabase
          .from('parameters')
          .select('parameter, value')
          .in('parameter', ['event_cost', 'credit_amount']);

        if (error) throw error;
        if (data) {
          const eventCostParam = data.find(p => p.parameter === 'event_cost');
          const creditAmountParam = data.find(p => p.parameter === 'credit_amount');
          
          setEventCost(eventCostParam?.value || 1);
          setCreditAmount(creditAmountParam?.value || null);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des paramètres:', error);
        setEventCost(1); // Valeur par défaut
      } finally {
        setLoading(false);
      }
    };

    fetchParameters();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex items-center justify-center">
        <p className="text-gray-500">{t('createEvent.loadingPrice')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <div className="flex flex-col items-center justify-center gap-2">
        <p className="text-lg font-semibold text-bleu2">
          {t('createEvent.cost')}
        </p>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-bleu2">
            {eventCost}
          </span>          
          <CoinIcon className="w-8 h-8" />
        </div>
        {creditAmount && (
          <p className="text-sm text-gray-600">
            1 <CoinIcon className="w-4 h-4 inline" /> = {creditAmount} €
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Composant de formulaire de création d'événement
 */
const EventForm = ({ currentUser, creditsBalance, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [eventCost, setEventCost] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    age_group_id: '',
    is_paid: false,
    external_url: '',
    image_url: '',
    is_accessible: false,
    weather_dependent: false,
    boost: true,
    location_name: '',
    formatted_address: '',
    street_number: '',
    street_name: '',
    postal_code: '',
    city: '',
    region: '',
    country: '',
    latitude: null,
    longitude: null,
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    accept_terms: false
  });

  // Charger les catégories, groupes d'âge et coût d'événement
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, ageGroupsRes, eventCostRes] = await Promise.all([
          supabase.from('categories').select('*').order('name'),
          supabase.from('age_groups').select('*').order('id'),
          supabase.from('parameters').select('value').eq('parameter', 'event_cost').single()
        ]);

        if (categoriesRes.data) setCategories(categoriesRes.data);
        if (ageGroupsRes.data) setAgeGroups(ageGroupsRes.data);
        if (eventCostRes.data) setEventCost(eventCostRes.data.value || 1);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setEventCost(1); // Valeur par défaut en cas d'erreur
      }
    };

    fetchData();
  }, []);

  // Calculer automatiquement la date de fin à partir de la date de début
  useEffect(() => {
    if (formData.start_date && !formData.end_date) {
      setFormData(prev => ({
        ...prev,
        end_date: prev.start_date
      }));
    }
  }, [formData.start_date]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Si on change la date de début, mettre à jour aussi la date de fin
    if (name === 'start_date') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        end_date: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleAddressChange = (addressData) => {
    setFormData(prev => ({
      ...prev,
      ...addressData
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Rafraîchir le solde de crédits juste avant la vérification
      const { data: creditsData, error: creditsError } = await supabase
        .from('credits')
        .select('credits')
        .eq('id', currentUser.id)
        .single();

      if (creditsError) {
        console.error('Erreur lors de la récupération du solde:', creditsError);
        throw new Error(t('errors.failedToFetchBalance'));
      }

      const currentBalance = creditsData?.credits || 0;

      // Vérifier le solde de crédits AVANT toute opération
      if (currentBalance < eventCost) {
        alert(t('createEvent.noCredits') + '\n' + t('createEvent.noCreditsDescription'));
        setLoading(false);
        navigate('/settings');
        return;
      }

      // Vérifier que nous avons une adresse avec coordonnées
      if (!formData.latitude || !formData.longitude) {
        alert(t('common.error') + ': ' + t('event.location'));
        setLoading(false);
        return;
      }

      // Préparer les données de l'événement
      const eventData = {
        title: formData.title,
        description: formData.description,
        category_id: formData.category_id,
        age_group_id: formData.age_group_id || null,
        is_paid: formData.is_paid,
        external_url: formData.external_url || null,
        image_url: formData.image_url || null,
        is_accessible: formData.is_accessible,
        weather_dependent: formData.weather_dependent,
        boost: formData.boost,
        location_name: formData.location_name,
        formatted_address: formData.formatted_address,
        street_number: formData.street_number || null,
        street_name: formData.street_name || null,
        postal_code: formData.postal_code,
        city: formData.city,
        region: formData.region || null,
        country: formData.country || 'France',
        latitude: formData.latitude,
        longitude: formData.longitude,
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        timezone: 'Europe/Paris',
        organizer_id: currentUser.id,
        status: 'pending_approval',
        source: 'user'
      };

      // Appel à l'Edge Function pour créer l'événement de manière sécurisée
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error(t('errors.notAuthenticated') || 'Non authentifié');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create_event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          eventData,
          eventCost
        })
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('Erreur Edge Function:', result);
        
        // Gestion des erreurs spécifiques
        if (response.status === 402) {
          alert(t('createEvent.noCredits') + '\n' + t('createEvent.noCreditsDescription'));
          navigate('/settings');
          return;
        }
        
        throw new Error(result.error || t('errors.unknownError'));
      }

      console.log('Événement créé avec succès:', result.event.id);
      const insertedEvent = result.event;
      const newBalance = result.newBalance;

      // Rediriger vers My Events avec l'onglet myEvents activé
      navigate('/my-events', { state: { activeTab: 'myEvents' } });
      
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert(t('common.error') + ': ' + (error.message || t('errors.unknownError')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6 mb-20">
      {/* Informations de base */}
      <div className="space-y-4">
        <h3 className="text-h3 text-bleu2">{t('event.details')}</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('event.title')} *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            maxLength={100}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('event.description')} *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            maxLength={400}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.description.length}/400
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('filters.categories')} *
            </label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
            >
              <option value="">{t('filters.allCategories')}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {t(`categories.${cat.name}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('event.ageGroup')}
            </label>
            <select
              name="age_group_id"
              value={formData.age_group_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
            >
              <option value="">{t('filters.allAgeGroups')}</option>
              {ageGroups.map(group => (
                <option key={group.id} value={group.id}>
                  {t(`ageGroups.${group.name}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('event.externalURL')}
          </label>
          <input
            type="url"
            name="external_url"
            value={formData.external_url}
            onChange={handleChange}
            maxLength={255}
            placeholder="https://..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL de l'image
          </label>
          <input
            type="url"
            name="image_url"
            value={formData.image_url}
            onChange={handleChange}
            maxLength={500}
            placeholder="https://..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
          {formData.image_url && (
            <div className="mt-2">
              <img 
                src={formData.image_url} 
                alt="Aperçu"
                className="w-full h-48 object-cover rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Dates et heures */}
      <div className="space-y-4">
        <h3 className="text-h3 text-bleu2">{t('event.date')} & {t('event.time')}</h3>
        
        <div className="grid grid-cols-1 gap-3">
          {/* Date de début */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('filters.startDate')} *
            </label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              required
              className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
            />
          </div>

          {/* Date de fin (disabled) */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              {t('filters.endDate')}
            </label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              readOnly
              disabled
              className="w-full px-2 py-2 text-sm border border-gray-200 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
            />
          </div>

          {/* Heure de début */}
          <div className="pt-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('event.time')} {t('filters.startDate')}
            </label>
            <input
              type="time"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
            />
          </div>

          {/* Heure de fin */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('event.time')} {t('filters.endDate')}
            </label>
            <input
              type="time"
              name="end_time"
              value={formData.end_time}
              onChange={handleChange}
              className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Localisation */}
      <div className="space-y-4">
        <h3 className="text-h3 text-bleu2">{t('event.location')}</h3>
         <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('event.address')} *
          </label>
          <AddressAutocomplete
            value={formData}
            onChange={handleAddressChange}
            required
          />
        </div>
        {formData.formatted_address && (
          <div className="p-4 bg-gray-50 rounded-lg space-y-2 text-sm text-gray-600">
            <p><span className="font-medium">Adresse :</span> {formData.street_number} {formData.street_name}</p>
            <p><span className="font-medium">Code postal :</span> {formData.postal_code}</p>
            <p><span className="font-medium">Ville :</span> {formData.city}</p>
            {formData.region && <p><span className="font-medium">Région :</span> {formData.region}</p>}
            <p><span className="font-medium">Pays :</span> {formData.country}</p>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('event.namelocation')} *
          </label>
          <input
            type="text"
            name="location_name"
            value={formData.location_name}
            onChange={handleChange}
            required
            maxLength={255}
            placeholder={t('createEvent.locationNamePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
        </div>




      </div>

      {/* Options */}
      <div className="space-y-4">
        <h3 className="text-h3 text-bleu2">Options</h3>
        
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="is_paid"
              checked={formData.is_paid}
              onChange={handleChange}
              className="w-4 h-4 text-bleu1 border-gray-300 rounded focus:ring-bleu1 accent-bleu1"
            />
            <span className="text-sm text-gray-700">{t('event.paid')}</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="is_accessible"
              checked={formData.is_accessible}
              onChange={handleChange}
              className="w-4 h-4 text-bleu1 border-gray-300 rounded focus:ring-bleu1 accent-bleu1"
            />
            <span className="text-sm text-gray-700">{t('event.accessible')}</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="weather_dependent"
              checked={formData.weather_dependent}
              onChange={handleChange}
              className="w-4 h-4 text-bleu1 border-gray-300 rounded focus:ring-bleu1 accent-bleu1"
            />
            <span className="text-sm text-gray-700">{t('event.weatherDependent')}</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="boost"
              checked={formData.boost}
              onChange={handleChange}
              className="w-4 h-4 text-bleu1 border-gray-300 rounded focus:ring-bleu1 accent-bleu1"
            />
            <span className="text-sm text-gray-700">{t('eventList.featured')}</span>
          </label>
        </div>
      </div>

      {/* Acceptation des CGU */}
      <div className="space-y-4 pt-4 border-t">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="accept_terms"
            checked={formData.accept_terms}
            onChange={handleChange}
            required
            className="w-4 h-4 mt-1 text-bleu1 border-gray-300 rounded focus:ring-bleu1 accent-bleu1"
          />
          <span className="text-sm text-gray-700">
            {t('terms.acceptTerms')} *{' '}
            <a
              href="/settings/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-bleu1 hover:underline"
            >
              {t('settings.help.terms')}
            </a>
          </span>
        </label>
      </div>

      {/* Boutons */}
      <div className="flex gap-4 pt-4 border-t">
        <button
          type="button"
          onClick={() => navigate('/my-events')}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-6 py-3 bg-bleu1 text-white rounded-lg hover:bg-bleu3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? t('common.loading') : t('common.save')}
        </button>
      </div>
    </form>
  );
};

/**
 * Page de création d'événement
 * Permet de créer un nouvel événement
 */
const CreateEventPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [creditsBalance, setCreditsBalance] = useState(null);
  const [profile, setProfile] = useState(null);
  const [eventCost, setEventCost] = useState(1);

  // Charger TOUTES les données en une seule fois
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      
      try {
        // 1. Récupérer l'utilisateur
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          // Si l'erreur est "Auth session missing", c'est normal (utilisateur non connecté)
          if (userError.message !== 'Auth session missing!') {
            console.error("Erreur lors de la récupération de l'utilisateur:", userError);
          }
          setCurrentUser(null);
          setCreditsBalance(null);
          setProfile(null);
          setEventCost(1);
          setLoading(false);
          return;
        }
        
        if (!user) {
          setCurrentUser(null);
          setCreditsBalance(null);
          setProfile(null);
          setEventCost(1);
          setLoading(false);
          return;
        }
        
        setCurrentUser(user);

        // 2. Charger en parallèle : profil, crédits et coût d'événement
        const [profileRes, creditsRes, eventCostRes] = await Promise.all([
          supabase
            .from('profiles')
            .select('organizer_name, organizer_lastname, organizer_firstname, organizer_email')
            .eq('id', user.id)
            .single(),
          supabase
            .from('credits')
            .select('credits')
            .eq('id', user.id)
            .single(),
          supabase
            .from('parameters')
            .select('value')
            .eq('parameter', 'event_cost')
            .single()
        ]);

        if (profileRes.error) throw profileRes.error;
        if (creditsRes.error) throw creditsRes.error;
        if (eventCostRes.error) throw eventCostRes.error;

        setCreditsBalance(creditsRes.data?.credits || 0);
        setProfile(profileRes.data);
        setEventCost(eventCostRes.data?.value || 1);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setCreditsBalance(0);
        setProfile(null);
        setEventCost(1);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Vérifier si le profil est incomplet
  const isProfileIncomplete = () => {
    if (!profile) return false;
    return !profile.organizer_name || 
           !profile.organizer_lastname || 
           !profile.organizer_firstname ||
           !profile.organizer_email;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-80px)]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-4 mb-40">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-h1 text-bleu2 mb-6">
            {t('createEvent.title')}
          </h1>
          
          <CreditBenefitsInfo />

          <LoginRequired />
        </div>
      </div>
    );
  }

  if (creditsBalance < eventCost) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-4 mb-40">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-h1 text-bleu2 mb-6">
            {t('createEvent.title')}
          </h1>
          
          <CreditBenefitsInfo />
          
          <EventCostDisplay />
          
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex items-center justify-center gap-2">
              <p className="text-base text-gray-700">
                {t('createEvent.yourBalance')} <span className="font-semibold text-bleu2">{creditsBalance}</span>
              </p>
              <CoinIcon className="w-6 h-6" />
            </div>
          </div>
          
          <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow p-8">
            <CardIcon className="w-16 h-16 mb-4 text-bleu1" />
            <h2 className="text-h1 text-center mb-4">{t('createEvent.noCredits')}</h2>
            <p className="text-body text-gray-600 text-center mb-6">{t('createEvent.noCreditsDescription')}</p>
            <button 
              onClick={() => navigate('/settings')}
              className="bg-[#2894D8] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#136E9D] transition-colors"
            >
              {t('createEvent.rechargeCredits')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isProfileIncomplete()) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-4 mb-40">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-h1 text-bleu2 mb-6">
            {t('createEvent.title')}
          </h1>
          
          <CreditBenefitsInfo />
          
          <EventCostDisplay />
          
          <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow p-8 mt-6">
            <ProfileIcon className="w-16 h-16 mb-4 text-bleu1 stroke-2" />
            <h2 className="text-h1 text-center mb-4">{t('createEvent.incompleteProfile')}</h2>
            <p className="text-body text-gray-600 text-center mb-6">{t('createEvent.incompleteProfileDescription')}</p>
            <button 
              onClick={() => navigate('/settings/profile')}
              className="bg-[#2894D8] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#136E9D] transition-colors"
            >
              {t('createEvent.completeProfile')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4 mb-40">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-h1 text-bleu2 mb-6">
          {t('createEvent.title')}
        </h1>
        
        <CreditBenefitsInfo />
        
        <EventCostDisplay />
        
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center justify-center gap-2">
            <p className="text-base text-gray-700">
              {t('createEvent.yourBalance')} <span className="font-semibold text-bleu2">{creditsBalance}</span>
            </p>
            <CoinIcon className="w-6 h-6" />
          </div>
        </div>
        
        <EventForm 
          currentUser={currentUser} 
          creditsBalance={creditsBalance}
        />
      </div>
    </div>
  );
};

export default CreateEventPage;